import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from 'components/Icon';
import { Appearance, theme, toRem } from 'design';
import { CentBasedInputCurrency } from '../Input/InputCurrency';
import Switch from '../Input/Switch';
import { Label, Text } from '../Typography';

const Layout = styled.section`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
`;

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${toRem(theme.space['2xs'])};

  p {
    /* off-scale exception: no font-size token below xxs (11px) */
    font-size: ${toRem(10)};
    color: ${theme.color.neutral[400]};
  }
`;

const InputWrapper = styled.div<{ $isError?: boolean; $disabled?: boolean }>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${toRem(theme.space['2xs'])};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(5.5)};
  height: ${toRem(35)};
  padding-right: ${toRem(theme.space.sm)};
  width: ${toRem(128)};

  input {
    border: none;
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      border: none;
    `}

  ${({ $isError }) =>
    $isError &&
    css`
      border-color: ${theme.color.danger[50]};
    `}
`;

const Indicator = styled.div<{ $borderColor: Appearance.SECONDARY | Appearance.DANGER; $borderPercent: number; $disabled?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  position: relative;
  box-sizing: border-box;
  width: ${toRem(18)};
  height: ${toRem(18)};
  border-radius: ${toRem(18)};

  &::before {
    content: '';
    position: absolute;
    inset: ${toRem(-0.5)};
    border-radius: 50%;
    background: ${({ $borderColor, $borderPercent }) => {
      const pct = Math.max(0, Math.min($borderPercent, 100));
      const activeColor = $borderColor === Appearance.SECONDARY ? theme.color.secondary[400] : theme.color.danger[50];
      return `conic-gradient(from 0deg, ${activeColor} 0% ${pct}%, ${theme.color.neutral[300]} ${pct}% 100%)`;
    }};

    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - ${toRem(2)} - ${toRem(0.6)}), rgba(255, 255, 255, 0.6) calc(100% - ${toRem(2)}), ${theme.color.white} calc(100% - ${toRem(2)} + ${toRem(0.6)}));
    mask: radial-gradient(farthest-side, transparent calc(100% - ${toRem(2)} - ${toRem(0.6)}), rgba(255, 255, 255, 0.6) calc(100% - ${toRem(2)}), ${theme.color.white} calc(100% - ${toRem(2)} + ${toRem(0.6)}));
  }

  svg {
    position: relative;
    z-index: 1;
    height: ${toRem(12)};
    width: ${toRem(12)};
  }

  ${({ $borderColor }) =>
    $borderColor === Appearance.SECONDARY &&
    css`
      svg {
        fill: ${theme.color.secondary[400]};

        &:hover {
          fill: ${theme.color.secondary[400]};
        }
      }
    `}

  ${({ $borderColor }) =>
    $borderColor === Appearance.DANGER &&
    css`
      svg {
        fill: ${theme.color.danger[50]};

        &:hover {
          fill: ${theme.color.danger[50]};
        }
      }
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      display: none;
    `}
`;

interface IPayoutLimitProps {
  total: number;
  limit?: number;
  isStrict?: boolean;
  dataCy?: string;
  onChange: (value?: string) => void;
}

const PayoutLimit = ({ onChange, total, limit, isStrict = false, dataCy = 'payout-limit' }: IPayoutLimitProps) => {
  const [value, setValue] = useState<number | undefined>(limit);
  const [inputActive, setInputActive] = useState<boolean>(isStrict || Number.isFinite(limit));

  const isAtLimit = !total ? false : total === (value || 0);
  const isOverLimit = total > (value || 0);
  const isDisabled = !inputActive && !isStrict;
  const inputValue = value === undefined ? '' : value.toString();
  const indicatorBorderColor = isOverLimit ? Appearance.DANGER : Appearance.SECONDARY;
  const indicatorPercent = value ? ((total || 0) / value) * 100 : 0; // Calculate the percentage of the total that the value represents

  // If the input is strict, set the value to 0
  useEffect(() => {
    if (isStrict && value === undefined) {
      setValue(0);
      onChange('0');
    }
  }, [isStrict, value]);

  // Handle input change
  const handleChange = (val: string) => {
    setValue(val === '' ? undefined : parseInt(val, 10));
    onChange(val);
  };

  // Handle switch change
  const handleSwitchChange = (checked: boolean) => {
    setInputActive(checked);
    // When the switch is on, set the value to 0 to default the value to 0
    if (checked) {
      setValue(0);
      onChange('0');
    } else {
      // When the switch is off, set the value to undefined to reset
      setValue(undefined);
      onChange(undefined);
    }
  };

  return (
    <Layout data-cy={dataCy}>
      <Wrapper>
        <Label data-cy={`${dataCy}-label`}>Payout Limit</Label>
        {isStrict && (
          <Text data-cy={`${dataCy}-required`} size="xsmall">
            (Required)
          </Text>
        )}
      </Wrapper>
      {!isStrict && <Switch checked={inputActive} onChange={() => handleSwitchChange(!inputActive)} data-cy={`${dataCy}-switch`} />}
      <InputWrapper data-cy={`${dataCy}-input-wrapper`} $disabled={isDisabled} $isError={isOverLimit}>
        <CentBasedInputCurrency height={33} maxCurrencyLength={7} disabled={isDisabled} value={inputValue} onChange={(val: string) => handleChange(val)} data-cy={`${dataCy}-input`} allowZero={inputActive} />

        <Indicator data-cy={`${dataCy}-indicator`} $borderColor={indicatorBorderColor} $borderPercent={isOverLimit ? 100 : indicatorPercent} $disabled={isDisabled} data-tooltip-id={`${dataCy}-indicator-tooltip`}>
          {isAtLimit && <Icon icon="CheckmarkThick" dataCy={`${dataCy}-indicator-check`} />}
          {isOverLimit && <Icon icon="Close" dataCy={`${dataCy}-indicator-close`} />}
        </Indicator>
      </InputWrapper>
    </Layout>
  );
};

export default PayoutLimit;
